import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  PhoneIncoming, PhoneOutgoing, MessageCircle, ArrowRight,
  Upload, Headphones, CalendarCheck, Check, Zap,
  Clock, ShieldCheck, TrendingUp, ChevronDown, ChevronUp,
  Brain, BarChart3, Building2, Users, Bot, Phone,
} from 'lucide-react';

const HERO_HEADLINE = "Il tuo team commerciale, potenziato dall'AI.";
const HERO_SUB = "Elios risponde, richiama e ricontatta i tuoi lead — al telefono e su WhatsApp. Tu chiudi le trattative.";

const STATS = [
  { value: '500+', label: 'chiamate gestite ogni mese' },
  { value: '92%',  label: 'tasso di risposta' },
  { value: '24/7', label: 'sempre operativo' },
];

const CAPABILITIES = [
  {
    icon: PhoneIncoming,
    title: "Voce inbound",
    desc: "Risponde al telefono come un commerciale esperto, 24 ore su 24. Qualifica il contatto e fissa l'appuntamento in agenda.",
  },
  {
    icon: PhoneOutgoing,
    title: "Outbound automatico",
    desc: "Chiama la tua rubrica e i tuoi lead inascoltati. Segue uno script che decidi tu, in italiano, con voce naturale.",
  },
  {
    icon: MessageCircle,
    title: "Follow-up WhatsApp",
    desc: "Manda follow-up personalizzati e risponde alle conversazioni in chat. Niente lead dimenticato.",
  },
];

const STEPS = [
  {
    icon: Upload,
    title: "1. Carichi i tuoi lead",
    desc: "Importi rubrica o lista contatti. Bastano pochi minuti.",
  },
  {
    icon: Headphones,
    title: "2. Elios chiama",
    desc: "Parla con i contatti, qualifica e prende appuntamenti secondo il tuo prompt.",
  },
  {
    icon: CalendarCheck,
    title: "3. Tu chiudi",
    desc: "Trovi gli appuntamenti già in calendario. Ti concentri solo sulle trattative pronte.",
  },
];

const DIFFERENTIATORS = [
  {
    icon: Brain,
    title: "Voce neurale 100% italiana",
    desc: "Non è un risponditore automatico né un chatbot. Parla in italiano con tono naturale e cordiale, adattandosi al contesto di ogni conversazione.",
  },
  {
    icon: Zap,
    title: "Chiamate parallele illimitate",
    desc: "Nessuna coda d'attesa. Elios gestisce decine di conversazioni simultanee senza abbassare la qualità del dialogo.",
  },
  {
    icon: ShieldCheck,
    title: "Trasferimento intelligente",
    desc: "Quando il cliente ha bisogno di un umano, Elios capisce il momento giusto e trasferisce la chiamata senza frizioni.",
  },
  {
    icon: BarChart3,
    title: "Analytics e trascrizioni",
    desc: "Ogni conversazione viene registrata, trascritta e analizzata dall'AI. Sai sempre cosa hanno detto i tuoi clienti.",
  },
  {
    icon: Clock,
    title: "Sempre operativo, H24",
    desc: "Nessun giorno di ferie, nessun orario di chiusura. Elios risponde anche la domenica sera.",
  },
  {
    icon: TrendingUp,
    title: "Setup in poche ore",
    desc: "Si integra con il tuo calendario e CRM velocemente. Nessuna installazione complessa, nessun tecnico richiesto.",
  },
];

const SECTORS = [
  {
    icon: Building2,
    title: "Agenzie immobiliari",
    desc: "Qualifica le richieste di appartamenti e fissa le visite in automatico, anche fuori orario.",
  },
  {
    icon: Users,
    title: "PMI e reti vendita",
    desc: "Richiama i lead non risposti e aumenta il tasso di contatto senza assumere nuovo personale.",
  },
  {
    icon: Phone,
    title: "Centralini aziendali",
    desc: "Sostituisce o affianca il centralino tradizionale, gestendo smistamento e prima accoglienza H24.",
  },
  {
    icon: Bot,
    title: "Studi professionali",
    desc: "Gestisce prenotazioni e risponde alle domande frequenti, liberando il team dalle chiamate di routine.",
  },
];

const FAQS = [
  {
    q: "Elios è difficile da configurare?",
    a: "No. Il sistema viene configurato e consegnato in pochi giorni, già integrato con i tuoi sistemi esistenti. Non è richiesta nessuna competenza tecnica da parte tua.",
  },
  {
    q: "Riesce a capire diversi accenti o dialetti regionali?",
    a: "Sì. Il motore neurale di Elios è addestrato per comprendere le variazioni regionali dell'italiano. Si adatta al lessico del tuo settore e ai pattern di comunicazione del tuo team.",
  },
  {
    q: "Elios può sostituire completamente il mio centralino?",
    a: "In molte aziende sì. In altri casi lavora in sinergia con il team umano, gestendo la prima accoglienza e i casi semplici. La configurazione è completamente personalizzabile.",
  },
  {
    q: "Ci sono limiti al numero di chiamate che può gestire?",
    a: "No. Elios gestisce un numero illimitato di conversazioni parallele senza ridurre la qualità. Non esistono code d'attesa: ogni cliente viene servito in tempo reale.",
  },
  {
    q: "I dati delle conversazioni sono al sicuro?",
    a: "Sì. Tutte le registrazioni e le trascrizioni sono archiviate in modo sicuro. La piattaforma è conforme al GDPR e i dati non vengono mai condivisi con terze parti.",
  },
];

const EliosLandingPage = () => {
  const navigate = useNavigate();
  const goLogin = () => navigate('/elios/login');
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-emerald-600 text-white px-3 py-1 rounded">
        Vai al contenuto
      </a>

      {/* Top bar */}
      <header className="border-b border-zinc-100 sticky top-0 bg-white/95 backdrop-blur z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/elios-logo-new.svg" alt="Elios" className="h-[4.5rem] w-auto" />
            <span className="text-xs uppercase tracking-wider text-zinc-500 hidden sm:inline">AI Voice CRM</span>
          </div>
          <Button onClick={goLogin} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9">
            Accedi
          </Button>
        </div>
      </header>

      <main id="main">
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 pt-20 pb-24 md:pt-28 md:pb-32">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium mb-6">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Piattaforma AI per agenzie e PMI
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-[1.1]">
                {HERO_HEADLINE}
              </h1>
              <p className="mt-6 text-lg text-zinc-600 leading-relaxed">
                {HERO_SUB}
              </p>
              <div className="mt-10 flex flex-col sm:flex-row items-start gap-3">
                <Button onClick={goLogin} className="bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-6 text-base gap-2">
                  Inizia ora <ArrowRight className="w-4 h-4" />
                </Button>
                <a href="#come-funziona">
                  <Button variant="ghost" className="h-12 px-6 text-base text-zinc-700 hover:bg-zinc-50">
                    Scopri come
                  </Button>
                </a>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-500">
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />Nessuna installazione</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />Setup in poche ore</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />Conforme GDPR</span>
              </div>
            </div>

            {/* Hero visual – mock conversation */}
            <div className="hidden md:flex flex-col gap-3">
              <div className="bg-zinc-900 rounded-2xl p-5 shadow-2xl">
                <div className="flex items-center gap-2.5 mb-5">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-sm text-emerald-400 font-medium">Elios · In chiamata</span>
                  <span className="ml-auto text-xs text-zinc-500 font-mono">00:42</span>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center shrink-0 text-xs text-zinc-300 font-medium">C</div>
                    <div className="bg-zinc-800 rounded-xl rounded-tl-sm px-3.5 py-2.5 text-zinc-200 max-w-[82%]">
                      Buongiorno, ho visto l'annuncio dell'appartamento in Via Roma…
                    </div>
                  </div>
                  <div className="flex gap-2.5 justify-end">
                    <div className="bg-emerald-600 rounded-xl rounded-tr-sm px-3.5 py-2.5 text-white max-w-[82%]">
                      Buongiorno! Sono Elios dell'agenzia. Ha già un'idea di budget o zona?
                    </div>
                    <div className="w-7 h-7 rounded-full bg-emerald-700 flex items-center justify-center shrink-0 text-xs text-white font-bold">E</div>
                  </div>
                  <div className="flex gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center shrink-0 text-xs text-zinc-300 font-medium">C</div>
                    <div className="bg-zinc-800 rounded-xl rounded-tl-sm px-3.5 py-2.5 text-zinc-200 max-w-[82%]">
                      Siamo intorno ai 200.000€. Vorremmo visitarlo questa settimana.
                    </div>
                  </div>
                  <div className="flex gap-2.5 justify-end">
                    <div className="bg-emerald-600 rounded-xl rounded-tr-sm px-3.5 py-2.5 text-white max-w-[82%]">
                      Perfetto. Ho disponibilità giovedì alle 15:00 o sabato mattina. Le va bene?
                    </div>
                    <div className="w-7 h-7 rounded-full bg-emerald-700 flex items-center justify-center shrink-0 text-xs text-white font-bold">E</div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {STATS.map(({ value, label }) => (
                  <div key={label} className="bg-zinc-50 border border-zinc-100 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-emerald-600">{value}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5 leading-tight">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Perché Elios è diverso */}
        <section className="bg-zinc-50 border-y border-zinc-100 py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Perché Elios è diverso</h2>
              <p className="mt-3 text-zinc-600 max-w-xl mx-auto">
                Non è un risponditore automatico, un chatbot o un call center tradizionale. È un collaboratore digitale che interagisce come una persona e lavora senza limiti.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-x-8 gap-y-10">
              {DIFFERENTIATORS.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shrink-0 mt-0.5">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-900 mb-1">{title}</h3>
                    <p className="text-sm text-zinc-600 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cosa fa Elios */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Cosa fa Elios</h2>
              <p className="mt-3 text-zinc-600 max-w-xl mx-auto">
                Tre canali, un'unica piattaforma. Pensata per agenzie immobiliari e piccole imprese italiane.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {CAPABILITIES.map(({ icon: Icon, title, desc }) => (
                <Card key={title} className="border-zinc-200 hover:border-emerald-300 hover:shadow-sm transition-all">
                  <CardContent className="p-6">
                    <div className="w-11 h-11 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 mb-4">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{title}</h3>
                    <p className="text-sm text-zinc-600 leading-relaxed">{desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Come funziona */}
        <section id="come-funziona" className="bg-zinc-50 border-y border-zinc-100 py-24">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Come funziona</h2>
              <p className="mt-3 text-zinc-600">Da zero a primo appuntamento in tre passi.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {STEPS.map(({ icon: Icon, title, desc }, i) => (
                <div key={title} className="text-center px-2 relative">
                  {i < STEPS.length - 1 && (
                    <div className="hidden md:block absolute top-7 left-[calc(50%+3rem)] w-[calc(100%-6rem)] h-px bg-emerald-200" />
                  )}
                  <div className="w-14 h-14 rounded-full bg-emerald-600 text-white flex items-center justify-center mx-auto mb-5 shadow-sm relative z-10">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{title}</h3>
                  <p className="text-sm text-zinc-600 leading-relaxed max-w-xs mx-auto">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Numbers strip */}
        <section className="bg-zinc-900 text-white py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
              {STATS.map(({ value, label }) => (
                <div key={label}>
                  <div className="text-5xl md:text-6xl font-bold text-emerald-400 mb-2">{value}</div>
                  <div className="text-sm uppercase tracking-wider text-zinc-400">{label}</div>
                </div>
              ))}
            </div>
            <div className="mt-12 pt-8 border-t border-zinc-800 text-center">
              <ul className="inline-flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-zinc-300">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" />Voce naturale in italiano</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" />Integrazione con il tuo calendario</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" />Nessuna installazione</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" />Conforme GDPR</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Per chi è pensato */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Per chi è pensato</h2>
              <p className="mt-3 text-zinc-600 max-w-xl mx-auto">
                Elios si adatta a qualsiasi realtà che gestisce telefonate e richieste commerciali.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-5">
              {SECTORS.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="border border-zinc-200 rounded-xl p-5 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all group">
                  <Icon className="w-7 h-7 text-zinc-400 group-hover:text-emerald-600 transition-colors mb-3" />
                  <h3 className="font-semibold text-zinc-900 mb-1">{title}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-zinc-50 border-y border-zinc-100 py-20">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Domande frequenti</h2>
              <p className="mt-3 text-zinc-600">Tutto quello che vuoi sapere su Elios.</p>
            </div>
            <div className="space-y-2">
              {FAQS.map(({ q, a }, i) => (
                <div key={i} className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between px-5 py-4 text-left gap-4 hover:bg-zinc-50 transition-colors"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="font-medium text-zinc-900">{q}</span>
                    {openFaq === i
                      ? <ChevronUp className="w-4 h-4 text-zinc-400 shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0" />}
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 pt-3 text-sm text-zinc-600 leading-relaxed border-t border-zinc-100">
                      {a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Pronto a far parlare la tua AI?</h2>
            <p className="mt-4 text-zinc-600 text-lg">Accedi al tuo spazio Elios e inizia subito.</p>
            <div className="mt-8">
              <Button onClick={goLogin} className="bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-8 text-base gap-2">
                Accedi alla piattaforma <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            <p className="mt-5 text-sm text-zinc-400">
              Già cliente?{' '}
              <button onClick={goLogin} className="text-emerald-600 hover:underline font-medium">
                Accedi qui
              </button>
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-zinc-100 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-zinc-500">
          <div className="flex items-center gap-2">
            <img src="/elios-logo-new.svg" alt="Elios" className="h-[4.5rem] w-auto" />
            <span>© {new Date().getFullYear()} Elios</span>
          </div>
          <button onClick={goLogin} className="hover:text-emerald-600 transition-colors">
            Accedi
          </button>
        </div>
      </footer>
    </div>
  );
};

export default EliosLandingPage;
